import { useEffect, useState } from 'react';
import { Sparkles, X, MessageCircle } from 'lucide-react';
import api from '@/lib/api';
import { useTenantAIConfig } from '@/hooks/useTenantAIConfig';
import { useAuth } from '@/lib/auth';

const DISMISS_KEY = 'valnexo_uatu_welcome_dismissed';

/**
 * Banner "Welcome Uatu" — s'affiche au premier login d'un admin_full sur un
 * tenant qui n'a pas encore configuré la doctrine. Propose de démarrer un
 * questionnaire conversationnel de 15 questions avec Uatu.
 * L'admin peut skipper (dismissed en localStorage) ou lancer le flow.
 */
export default function UatuWelcomeBanner() {
    const { user } = useAuth();
    const tenantAI = useTenantAIConfig();
    const [needs, setNeeds] = useState(false);
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
    });

    useEffect(() => {
        if (!user || user.role !== 'admin_full' || dismissed) return;
        (async () => {
            try {
                const r = await api.get('/tenant/ai-config/onboarding-state');
                setNeeds(!!r.data?.needs_onboarding);
            } catch { /* silent */ }
        })();
    }, [user, dismissed]);

    if (!needs || dismissed || user?.role !== 'admin_full') return null;

    const startFlow = () => {
        // Injecte le premier message dans le chat via un CustomEvent
        try {
            const seed = (
                `Salut ${tenantAI.assistant_name} ! Aide-moi à te configurer pour mon entreprise. ` +
                `Pose-moi tes 15 questions pour cerner ma doctrine, mon ton, mes KPIs, mes modules, ` +
                `mes règles métier et le secteur d'activité. On y va une question à la fois, et tu ` +
                `utiliseras \`propose_doctrine_update\` au fur et à mesure de mes réponses.`
            );
            window.dispatchEvent(new CustomEvent('uatu:open-with-message', { detail: { message: seed, autoSend: true } }));
        } catch { /* noop */ }
    };

    const skip = () => {
        setDismissed(true);
        try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* noop */ }
    };

    const done = async () => {
        try { await api.post('/tenant/ai-config/onboarding-complete'); } catch { /* noop */ }
        setNeeds(false);
    };

    return (
        <div
            className="rounded-2xl border border-violet-300 bg-gradient-to-r from-violet-50 to-indigo-50 p-4 flex flex-col sm:flex-row items-start gap-3 shadow-sm relative"
            data-testid="uatu-welcome-banner"
        >
            <button
                type="button"
                onClick={skip}
                className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                aria-label="Ignorer"
                data-testid="uatu-welcome-skip"
            >
                <X className="w-4 h-4" />
            </button>
            <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 text-white"
                style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)' }}
            >
                <Sparkles className="w-6 h-6" />
            </div>
            <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">
                    Bienvenue ! Je suis {tenantAI.assistant_name}, ton assistant IA.
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                    On peut se configurer ensemble en 15 questions rapides — doctrine, ton, KPIs, règles métier.
                    Je m&apos;adapte à ton entreprise pendant qu&apos;on discute.
                </p>
            </div>
            <div className="flex gap-2 flex-shrink-0 mt-2 sm:mt-0">
                <button
                    type="button"
                    onClick={startFlow}
                    data-testid="uatu-welcome-start"
                    className="rounded-xl px-3 py-2 text-white text-xs font-semibold shadow flex items-center gap-1.5"
                    style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #6366F1 100%)' }}
                >
                    <MessageCircle className="w-3.5 h-3.5" /> Démarrer
                </button>
                <button
                    type="button"
                    onClick={done}
                    data-testid="uatu-welcome-done"
                    className="rounded-xl px-3 py-2 text-slate-600 hover:text-slate-800 text-xs font-semibold"
                >
                    Déjà fait
                </button>
            </div>
        </div>
    );
}
